import { commonStateAtom, keyAid, keyBid, keyCid, keyDid } from "../common";
import { headerAtom } from "../app";
import { filterEmpty } from "@/utils";

/** URL 上是否带有层级 key（与各处原条件一致） */
export function hasUrlHierarchyKeys() {
  return !!(keyAid || keyBid || keyCid || keyDid);
}

/**
 * 子页/列表类接口：与 SELECT_CITY、首页状态、缓存 INFO 对齐 keyId（原逻辑不变）
 * homeAtom 由调用方传入，避免与 home 模块循环依赖
 */
export function assignResolvedKeyIdForSubPageQueries(get: (a: any) => any, newParams: any, homeAtom: any) {
  const locSelCity = LocalStorage.getItem("SELECT_CITY");
  const locInfo = LocalStorage.getItem("INFO");
  const homeData = get(homeAtom);
  newParams.keyId = newParams.keyId || locSelCity?.Key || homeData?.data?.info?.key || locInfo?.key;
}

/** 正在刷新定位时清空层级 key 参数（原逻辑不变） */
export function clearKeyAidBidCidDidWhenRefreshingLoc(newParams: any, refreshloc: boolean) {
  if (!refreshloc) return;
  newParams.keyAid = undefined;
  newParams.keyBid = undefined;
  newParams.keyCid = undefined;
  newParams.keyDid = undefined;
}

/** 将 store 中的 keyDid 写回请求参数（原逻辑不变） */
export function assignKeyDidToParams(newParams: any, didVal: any) {
  if (didVal) {
    newParams.keyDid = didVal;
  }
}

/** dkweather 域名下 /daily 首页增加 count（仅 daily 使用，原逻辑不变） */
export function applyDkWeatherDailyHomepageCount(newParams: any, pathname: string) {
  const isDkWeatherDomain = window.location.host.includes("dkweather");
  const isDkWeatherHomepage = isDkWeatherDomain && ["/daily"].includes(pathname);
  if (isDkWeatherHomepage) {
    newParams.count = 15;
  }
}

/** 从接口返回的 info 对象拼 header 片段（子页接口走 resData.data.info） */
export function buildCompactHeaderFromInfo(info: any) {
  return {
    WeatherIconImg: info?.WeatherIconImg ?? "",
    WeatherText: info?.WeatherText ?? "",
    Temperature: info?.Temperature ?? {},
    Wind: info?.Wind ?? {},
    IconId: info?.IconId ?? "",
  };
}

/** 子页类：KEY_AID 使用 info.key 且空值落为 ""（与 alert/daily/hourly 等原逻辑一致） */
export function persistKeyAidFromSubPageResponseInfoKey(infoKey: any) {
  if (!hasUrlHierarchyKeys()) return;
  LocalStorage.setItem("KEY_AID", infoKey || "");
}

/** Home / loadLocation：KEY_AID 不使用 `|| ""`（与原逻辑一致） */
export function persistKeyAidFromHomeOrLocationResponseInfoKey(infoKey: any) {
  if (hasUrlHierarchyKeys() && infoKey) {
    LocalStorage.setItem("KEY_AID", infoKey);
  }
}

/** 子页：INFO 写入 LocalStorage，空值用 {}（与原逻辑一致） */
export function persistInfoLocalStorageSubPageStyle(info: any) {
  LocalStorage.setItem("INFO", info || {});
}

/** Home：INFO 原样写入（无 `|| {}`） */
export function persistInfoLocalStorageHomeStyle(info: any) {
  LocalStorage.setItem("INFO", info);
}

/** 仅把接口 info 同步到 commonStateAtom.data（子页列表类共用） */
export function setCommonStateDataInfoOnly(set: any, info: any) {
  set(commonStateAtom, (prev: any) => ({
    ...prev,
    data: {
      info,
    },
  }));
}

/** 更新 headerAtom 并写入 HEADER2（各接口共用） */
export function applyCompactHeaderToAtomAndStorage(set: any, resHeader: ReturnType<typeof buildCompactHeaderFromInfo>) {
  if (!isEmptyType(filterEmpty(resHeader))) {
    set(headerAtom, (prev: any) => ({
      ...prev,
      ...resHeader,
    }));
    LocalStorage.setItem("HEADER2", resHeader);
  }
}

/**
 * 子页标准顺序：KEY_AID → INFO → commonState.data.info → header（与 alert/daily/hourly 等原顺序一致）
 * rahuKaal 不使用本函数（其 INFO 写在 commonState 之后）
 */
export function persistSubpageApiInfoAndHeader(set: any, info: any) {
  persistKeyAidFromSubPageResponseInfoKey(info?.key);
  persistInfoLocalStorageSubPageStyle(info);
  setCommonStateDataInfoOnly(set, info);
  const resHeader = buildCompactHeaderFromInfo(info);
  applyCompactHeaderToAtomAndStorage(set, resHeader);
}
